"use client";

import type { BoardToolName } from "@magnus/contract/boards";
import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

/**
 * Boards capability tool views (ADR-122). Reads are `step` rows in the
 * research group; `createBoard` / `updateBoard` are standalone link cards;
 * the other mutations are compact `step` rows under `create`.
 */

const views = () => import("./views");

export const boardsToolRegistry = {
  listBoards: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      views().then(({ ListBoardsToolView }) => ({
        default: ListBoardsToolView,
      }))
    ),
  },
  readBoard: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./read-board").then(({ ReadBoardToolView }) => ({
        default: ReadBoardToolView,
      }))
    ),
  },
  createBoard: {
    kind: "standalone",
    category: "create",
    view: lazy(() =>
      views().then(({ CreateBoardToolView }) => ({
        default: CreateBoardToolView,
      }))
    ),
  },
  updateBoard: {
    kind: "standalone",
    category: "create",
    view: lazy(() =>
      views().then(({ UpdateBoardToolView }) => ({
        default: UpdateBoardToolView,
      }))
    ),
  },
  moveBoard: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ MoveBoardToolView }) => ({
        default: MoveBoardToolView,
      }))
    ),
  },
  deleteBoard: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ DeleteBoardToolView }) => ({
        default: DeleteBoardToolView,
      }))
    ),
  },
  addBoardFields: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ AddBoardFieldsToolView }) => ({
        default: AddBoardFieldsToolView,
      }))
    ),
  },
  updateBoardField: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ UpdateBoardFieldToolView }) => ({
        default: UpdateBoardFieldToolView,
      }))
    ),
  },
  deleteBoardField: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ DeleteBoardFieldToolView }) => ({
        default: DeleteBoardFieldToolView,
      }))
    ),
  },
  setBoardFieldOptions: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ SetBoardFieldOptionsToolView }) => ({
        default: SetBoardFieldOptionsToolView,
      }))
    ),
  },
  setBoardViews: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ SetBoardViewsToolView }) => ({
        default: SetBoardViewsToolView,
      }))
    ),
  },
  writeBoardRecords: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ WriteBoardRecordsToolView }) => ({
        default: WriteBoardRecordsToolView,
      }))
    ),
  },
  deleteBoardRecords: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ DeleteBoardRecordsToolView }) => ({
        default: DeleteBoardRecordsToolView,
      }))
    ),
  },
  reorderBoardRecords: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ ReorderBoardRecordsToolView }) => ({
        default: ReorderBoardRecordsToolView,
      }))
    ),
  },
  importBoardCsv: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ ImportBoardCsvToolView }) => ({
        default: ImportBoardCsvToolView,
      }))
    ),
  },
  setBoardCreationLink: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      views().then(({ SetBoardCreationLinkToolView }) => ({
        default: SetBoardCreationLinkToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry & Record<BoardToolName, ToolUIMeta>;

/**
 * Namespaced variant keyed by the progressive-disclosure inner-tool name
 * `boards_${toolName}`. Boards is a progressive-disclosure capability
 * (ADR-057), so live tool parts arrive as e.g. `boards_readBoard`; the
 * bare-name entries above cover the static tier / exhaustiveness check.
 */
export const boardsProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(boardsToolRegistry).map(([name, meta]) => [
    `boards_${name}`,
    meta,
  ])
);
